import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { tryUseNuxtApp, useRuntimeConfig, navigateTo } from 'nuxt/app'
import { routeConfig } from '../config/manual-routes'
import { replaceRouteTemplateParams } from '../utils/replace-route-template-params'
import {
  isMeetShellPath,
  normalizePathnameForExternalRules,
  shouldAlwaysNavigateExternally,
  shouldNavigateExternally,
} from '../config/external-navigation'
import type { CPNavigationInput, CPNavigationTarget, NavigationState } from '../types/navigation'

/**
 * Prefer an explicit middleware/route context, then vue-router's current route via
 * the Nuxt app. Avoid `useRoute()` here — Nuxt 4 warns (NUXT_E2005) when it is
 * called from middleware or outside setup.
 */
const resolveActiveRoute = (currentRouteContext?: CPNavigationInput['currentRouteContext']) => {
  const liveRoute = tryUseNuxtApp()?.$router?.currentRoute?.value
  const path = currentRouteContext?.path ?? (typeof liveRoute?.path === 'string' ? liveRoute.path : '')
  const params = (currentRouteContext?.params ?? liveRoute?.params ?? {}) as Record<string, unknown>

  return { path, params }
}

/**
 * Split a full URL (path + optional query + hash) into a Vue Router location
 * object that can also carry `history.state`. Vue Router rejects query/hash
 * embedded in `path` — they must be separate fields.
 */
export const toLocationWithState = (url: string, state: NavigationState) => {
  const hashIndex = url.indexOf('#')
  const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : undefined

  const queryIndex = withoutHash.indexOf('?')
  const path = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash
  const queryString = queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : ''
  const queryEntries = queryString ? [...new URLSearchParams(queryString).entries()] : []
  const query = queryEntries.length > 0 ? Object.fromEntries(queryEntries) : undefined

  return {
    path,
    ...(query ? { query } : {}),
    ...(hash ? { hash } : {}),
    state,
  } as Parameters<typeof navigateTo>[0]
}

/**
 * Main CPNavigateTo function with object-style signature
 * This is the primary navigation function that should be used throughout the app.
 *
 * Note: this file used to also export `useNavigationContext` for the post-modal
 * "is this navigation internal?" flag, backed by a `globalThis.__navigationContext`
 * mutated from middleware. That pattern was replaced by `useModalNavigation`
 * (history.state-driven via `router.afterEach`) — see
 * `composables/useModalNavigation.ts` and `plugins/modal-navigation.client.ts`.
 */
export const useCPNavigation = () => {
  const { isMobileOrTablet } = useGlobal()
  /**
   * Detect context from current route
   */
  const detectContextFromRoute = (currentRouteContext?: CPNavigationInput['currentRouteContext']) => {
    const { isGokollab } = useGlobal()
    const { path, params: routeParams } = resolveActiveRoute(currentRouteContext)

    // Extract context parameters
    const groupSlug = routeParams.groupSlug as string
    const userSlug = routeParams.userSlug as string
    const productId = routeParams.productId as string

    // Detect current module based on path and parameters
    let currentModule: 'communities' | 'courses' | 'other' = 'other'

    if (path.startsWith('/courses/')) {
      currentModule = 'courses'
    } else if (path.startsWith('/communities')) {
      currentModule = 'communities'
    } else if (isGokollab && (groupSlug || userSlug)) {
      // In GoKollab mode, if we have groupSlug or userSlug, it's communities context
      currentModule = 'communities'
    }

    return {
      isGoKollab: isGokollab,
      currentModule,
      groupSlug,
      productId,
    }
  }

  /**
   * Validate required parameters for a route
   */
  const validateParams = (routeName: string, params: Record<string, unknown> = {}) => {
    const config = routeConfig[routeName]
    if (!config) {
      throw new Error(`Unknown route: ${routeName}`)
    }

    const missingParams = config.requiredParams.filter(param => !params[param])
    if (missingParams.length > 0) {
      throw new Error(`Missing required parameters for route '${routeName}': ${missingParams.join(', ')}`)
    }
  }

  /**
   * Resolve route path based on context and configuration
   */
  const resolveRoute = (
    routeName: string,
    params: Record<string, unknown> = {},
    getDefaultTemplate?: boolean,
    currentRouteContext?: CPNavigationInput['currentRouteContext']
  ) => {
    const config = routeConfig[routeName]
    if (!config) {
      throw new Error(`Unknown route: ${routeName}`)
    }

    const isCourseRoute = routeName.startsWith('courses-') || routeName === 'courses' || routeName === 'my-library'

    // Determine context based on the route being navigated to
    let template: string

    // if module name is provided, use it to determine the template
    if (getDefaultTemplate) {
      template = config.default
    } else {
      // Get current context only when context-aware resolution is required.
      const context = detectContextFromRoute(currentRouteContext)

      // In courses module, keep course navigation on /courses/* paths.
      if (context.currentModule === 'courses' && isCourseRoute) {
        template = config.default
      }
      // For courses routes, always use default template regardless of current context
      else if (config.courses) {
        template = config.default
      } else if (context.isGoKollab && config.gokollab) {
        // GoKollab mode: use gokollab template for all other routes
        template = config.gokollab
      } else if (config.communities) {
        // On mobile, prefer standalone /courses/* for course routes — unless navigation is scoped to a
        // group (groupSlug passed). Otherwise purchase/checkout from group Learning resolves to /courses/...
        // routes that are not mounted or redirect in the communities app (user lands on root).
        const isCoursesNamedRoute = routeName.startsWith('courses-') || routeName === 'my-library'
        const navigatesWithinGroup =
          typeof params.groupSlug === 'string' && params.groupSlug.length > 0 && config.communities.includes('[groupSlug]')
        if (process.client && isCoursesNamedRoute && isMobileOrTablet() && !navigatesWithinGroup) {
          template = config.default
        } else {
          template = config.communities
        }
      } else {
        // Fallback to default template
        template = config.default
      }
    }

    // Replace parameters in template (encoded per segment — see replaceRouteTemplateParams)
    return replaceRouteTemplateParams(template, params)
  }

  /**
   * Build query string from query parameters
   */
  const buildQueryString = (query?: Record<string, string | number | boolean | string[]>): string => {
    if (!query || Object.keys(query).length === 0) return ''

    const params = new URLSearchParams()

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(item => params.append(key, String(item)))
        } else {
          params.append(key, String(value))
        }
      }
    })

    const queryString = params.toString()
    return queryString ? `?${queryString}` : ''
  }

  /**
   * Runtime app env/name for navigation (SSR + client).
   */
  const getRuntimeNavigationConfig = () => {
    try {
      const config = useRuntimeConfig()
      return {
        appEnv: (config.public?.appEnv || 'development') as string,
        appName: (config.public?.appName || '') as string,
      }
    } catch {
      return {
        appEnv: 'development',
        appName: '',
      }
    }
  }

  /**
   * Returns appName and legacyCoursesHandoff from runtime config so callers
   * avoid duplicating the runtimeConfig read when deciding legacy vs SPA routes.
   */
  const getCoursesHandoffConfig = () => {
    const { appName, appEnv } = getRuntimeNavigationConfig()
    const legacyCoursesHandoff = useRuntimeConfig().public?.legacyCoursesHandoff as string | undefined
    return { appName, appEnv, legacyCoursesHandoff }
  }

  /**
   * Resolve the final navigation target for both imperative navigation and link rendering.
   */
  const getNavigationTarget = (
    input: CPNavigationInput,
    getDefaultTemplate?: boolean,
    forceExternal?: boolean,
    forceInternal?: boolean
  ): CPNavigationTarget => {
    const finalUrl = getRoutePath(input, getDefaultTemplate)
    const { appName, appEnv } = getRuntimeNavigationConfig()
    const externalNavContext = { appName, appEnv }

    const fromPath = normalizePathnameForExternalRules(resolveActiveRoute(input.currentRouteContext).path)
    const toPath = normalizePathnameForExternalRules(finalUrl)
    const stayInMeetShell = isMeetShellPath(fromPath) && isMeetShellPath(toPath)

    if (stayInMeetShell) {
      return {
        url: finalUrl,
        external: false,
      }
    }

    if (forceInternal) {
      return { url: finalUrl, external: false }
    }

    const shouldUseExternalNavigation =
      forceExternal ||
      shouldAlwaysNavigateExternally(finalUrl, undefined, externalNavContext) ||
      shouldNavigateExternally(finalUrl, undefined, externalNavContext)

    return {
      url: finalUrl,
      external: shouldUseExternalNavigation,
    }
  }

  /**
   * Main CPNavigateTo function
   */
  const CPNavigateTo = async (input: CPNavigationInput, getDefaultTemplate?: boolean, forceExternal?: boolean, forceInternal?: boolean) => {
    try {
      const target = getNavigationTarget(input, getDefaultTemplate, forceExternal, forceInternal)
      const options = { ...(input.options as Record<string, unknown>) }

      if (target.external) {
        options.external = true
      }

      // When the caller passes `state`, we must use the object form of navigateTo so the
      // payload reaches Vue Router's `router.push({ path, state })` and lands on the
      // history entry. Otherwise keep the existing string form for behaviour parity.
      //
      // Vue Router's `path` must not include `?query` or `#hash` — those belong in
      // separate fields. Putting the full URL (with query) into `path` drops
      // groupId/locationId on user-post modal navigations, so PostViewRouter never
      // gets postData and the modal shell stays empty.
      if (input.state) {
        return await navigateTo(toLocationWithState(target.url, input.state), options)
      }
      return await navigateTo(target.url, options)
    } catch (error) {
      console.error('CPNavigateTo failed:', error)
      throw error
    }
  }

  /**
   * Get route path based on input, without navigation
   * When you need to get the path for a link, without navigating to it
   */
  const getRoutePath = (input: CPNavigationInput, getDefaultTemplate?: boolean) => {
    validateParams(input.name, input.params)
    // Resolve route path
    const resolvedPath = resolveRoute(input.name, input.params, getDefaultTemplate, input.currentRouteContext)

    // Build query string and hash
    const queryString = buildQueryString(input.query)
    const hashPart = input.hash ? (input.hash.startsWith('#') ? input.hash : `#${input.hash}`) : ''
    const finalUrl = `${resolvedPath}${queryString}${hashPart}`
    return finalUrl
  }

  return {
    CPNavigateTo,
    getNavigationTarget,
    getRoutePath,
    validateParams,
    resolveRoute,
    getCoursesHandoffConfig,
  }
}
